import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

export const TYPE_META = {
  reel: { label: "Reel", cls: "bg-rose-50 text-rose-600 border-rose-200" },
  photo: { label: "Photo", cls: "bg-sky-50 text-sky-600 border-sky-200" },
  carrousel: { label: "Carrousel", cls: "bg-violet-50 text-violet-600 border-violet-200" },
  story: { label: "Story", cls: "bg-amber-50 text-amber-600 border-amber-200" },
  newsletter: { label: "Newsletter", cls: "bg-teal-50 text-teal-600 border-teal-200" },
  publicite: { label: "Publicité", cls: "bg-orange-50 text-orange-600 border-orange-200" },
  autre: { label: "Autre", cls: "bg-neutral-100 text-neutral-500 border-neutral-200" },
};
export const STATUS_META = {
  a_creer: { label: "À créer", cls: "bg-neutral-100 text-neutral-600 border-neutral-200" },
  shooting_a_programmer: { label: "Shooting à programmer", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  shooting_programme: { label: "Shooting programmé", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  en_production: { label: "En production", cls: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  finalise: { label: "Finalisé", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  pret_a_publier: { label: "Prêt à publier", cls: "bg-green-50 text-green-700 border-green-200" },
};
export const PUB_META = {
  non_creee: { label: "Non créée", cls: "text-neutral-300" },
  en_preparation: { label: "En préparation", cls: "text-neutral-500" },
  a_valider: { label: "À valider", cls: "text-amber-600" },
  modifications: { label: "Modifs demandées", cls: "text-orange-600" },
  validee: { label: "Validée", cls: "text-emerald-600" },
  programmee: { label: "Programmée", cls: "text-blue-600" },
  publiee: { label: "Publiée", cls: "text-green-700" },
  annulee: { label: "Annulée", cls: "text-red-400" },
};
export const HL_TYPES = { evenement: "Événement", offre: "Offre mensuelle", campagne: "Campagne", prestation: "Prestation / sujet", saison: "Opération saisonnière", objectif: "Objectif de communication" };
export const HL_STATUS_META = {
  a_preparer: { label: "À préparer", cls: "bg-neutral-100 text-neutral-600 border-neutral-200" },
  en_cours: { label: "En cours", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  termine: { label: "Terminé", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};
export const SHOOTING_STATUS_META = {
  planifie: { label: "Planifié", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  termine: { label: "Terminé", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  annule: { label: "Annulé", cls: "bg-red-50 text-red-500 border-red-200" },
};
export const fmtD = (d) => (d ? new Date(d + "T12:00:00").toLocaleDateString("fr-FR", { day: "numeric", month: "short" }) : "À définir");
export const fmtDur = (m) => (m ? `${Math.floor(m / 60)} h${m % 60 ? ` ${String(m % 60).padStart(2, "0")}` : ""}` : "—");
export const TODAY = new Date().toISOString().slice(0, 10);

/* Barre de progression événement par statut de publication :
   vert = validée/programmée/publiée, orange = créée/à valider, gris = à créer */
export const PUB_SEGMENTS = (items) => {
  const green = items.filter((i) => ["validee", "programmee", "publiee"].includes(i.publication_status)).length;
  const orange = items.filter((i) => ["en_preparation", "a_valider", "modifications"].includes(i.publication_status)).length;
  return { green, orange, gray: items.length - green - orange };
};

export function PubProgressBar({ items, testid }) {
  const { green, orange } = PUB_SEGMENTS(items);
  const total = items.length || 0;
  const pct = (n) => (total ? `${(n / total) * 100}%` : "0%");
  return (
    <div className="h-1.5 bg-neutral-100 rounded-full overflow-hidden flex" data-testid={testid}>
      <div className="h-full bg-emerald-500 transition-all" style={{ width: pct(green) }} />
      <div className="h-full bg-amber-400 transition-all" style={{ width: pct(orange) }} />
    </div>
  );
}

/* Statut dérivé d'un événement selon les publications de ses contenus */
export const EVENT_STAGES = {
  a_creer: { label: "À créer", cls: "bg-neutral-100 text-neutral-600 border-neutral-200" },
  cree: { label: "Créé", cls: "bg-sky-50 text-sky-700 border-sky-200" },
  a_valider: { label: "Envoyé pour validation", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  modifications: { label: "Modifications demandées", cls: "bg-orange-50 text-orange-700 border-orange-200" },
  validee: { label: "Validé", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  programmee: { label: "Programmée", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  publiee: { label: "Publiée", cls: "bg-green-50 text-green-700 border-green-200" },
};

export const eventStageKey = (items) => {
  const pubs = items.filter((i) => i.publication_status && i.publication_status !== "non_creee");
  if (pubs.some((i) => i.publication_status === "modifications")) return "modifications";
  if (pubs.some((i) => i.publication_status === "a_valider")) return "a_valider";
  if (pubs.some((i) => i.publication_status === "publiee")) return "publiee";
  if (pubs.some((i) => i.publication_status === "programmee")) return "programmee";
  if (pubs.some((i) => i.publication_status === "validee")) return "validee";
  if (pubs.length > 0) return "cree";
  if (items.some((i) => i.created)) return "cree";
  return "a_creer";
};

/* Un contenu compte comme « créé » s'il est coché OU si une publication existe déjà (envoyée pour validation ou au-delà) */
export const isCreated = (i) => !!i.created || (!!i.publication_status && i.publication_status !== "non_creee");

export const Badge = ({ meta, value }) => {
  const m = meta[value] || { label: value, cls: "bg-neutral-100 text-neutral-500 border-neutral-200" };
  return <span className={`text-[10px] font-medium border rounded-full px-2 py-0.5 whitespace-nowrap ${m.cls}`}>{m.label}</span>;
};

export const Label = ({ children }) => <label className="block text-[11px] font-medium text-neutral-500 mb-1">{children}</label>;
export const sel = "w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#1D4ED8]/30";

/* Sélecteur de créneaux réellement disponibles dans l'agenda agence */
export function SlotPicker({ endpoint, durationMin = 120, onPick, picked, testid = "slot-picker" }) {
  const [open, setOpen] = useState(false);
  const [slots, setSlots] = useState(null);
  const load = () => {
    setOpen(true);
    setSlots(null);
    api.get(endpoint, { params: { duration_min: durationMin } })
      .then((r) => setSlots(r.data || []))
      .catch((e) => { toast.error(apiError(e)); setSlots([]); });
  };
  return (
    <div>
      {!open ? (
        <Button type="button" variant="outline" size="sm" className="h-8 text-xs" onClick={load} data-testid={`${testid}-open`}>
          Voir les créneaux disponibles
        </Button>
      ) : (
        <div className="border border-neutral-200 rounded-xl p-2.5 bg-white" data-testid={testid}>
          <div className="flex items-center justify-between mb-2">
            <p className="text-[11px] font-semibold text-neutral-600">Créneaux libres de l'agenda ({fmtDur(durationMin)})</p>
            <button type="button" className="text-[10px] text-neutral-400 hover:text-neutral-700" onClick={() => setOpen(false)} data-testid={`${testid}-close`}>Masquer</button>
          </div>
          {slots === null && <p className="text-xs text-neutral-400 animate-pulse py-2">Chargement…</p>}
          {slots !== null && slots.length === 0 && (
            <p className="text-xs text-neutral-400 py-2" data-testid={`${testid}-empty`}>Aucun créneau disponible sur les 3 prochaines semaines avec cette durée.</p>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 max-h-44 overflow-y-auto">
            {(slots || []).map((sl) => {
              const active = picked && picked.date === sl.date && picked.start === sl.start;
              return (
                <button key={`${sl.date}-${sl.start}`} type="button" data-testid={`${testid}-slot-${sl.date}-${sl.start}`}
                  onClick={() => onPick(sl)}
                  className={`text-[10px] font-medium border rounded-lg px-2 py-1.5 transition-colors ${active ? "bg-[#0B132B] text-white border-[#0B132B]" : "bg-white text-neutral-600 border-neutral-200 hover:border-[#1D4ED8]/60"}`}>
                  {sl.label}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
